import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  ImageSourcePropType,
  Platform,
} from 'react-native';
import PlatformIcon from '../common/PlatformIcon';

// 直接定义颜色，避免 @cubemeet/core 导入问题
const MODULE_COLOR = '#F97316'; // travel 模块的橙色

// ============ 类型定义 ============

export interface MemberBalanceItem {
  /** 用户ID */
  userId: string;
  /** 显示名称 */
  displayName: string;
  /** 头像URL */
  avatarUrl?: string | null;
  /** 已支付金额 */
  paid: number;
  /** 应付金额 */
  shouldPay: number;
  /** 余额 (正数=多付, 负数=欠款) */
  balance: number;
}

export interface MemberBalanceSectionProps {
  /** 成员余额列表 */
  balances: MemberBalanceItem[];
  /** 货币符号 */
  currency?: string;
  /** 格式化金额 */
  formatCurrency?: (amount: number, currency?: string) => string;
  /** 获取头像 source */
  getAvatarSource?: (url: string) => ImageSourcePropType | null;
}

// ============ 默认格式化函数 ============

const defaultFormatCurrency = (amount: number, currency?: string): string => {
  const symbol = currency || 'HK$';
  return `${symbol}${amount.toFixed(2)}`;
};

// ============ 组件 ============

const MemberBalanceSection: React.FC<MemberBalanceSectionProps> = ({
  balances,
  currency = 'HK$',
  formatCurrency = defaultFormatCurrency,
  getAvatarSource,
}) => {
  if (balances.length === 0) {
    return null;
  }

  const renderAvatar = (member: MemberBalanceItem) => {
    const initial = (member.displayName || 'U').charAt(0).toUpperCase();

    if (member.avatarUrl && getAvatarSource) {
      const source = getAvatarSource(member.avatarUrl);
      if (source) {
        return <Image source={source} style={styles.avatar} />;
      }
    }

    return (
      <View style={[styles.avatar, styles.avatarPlaceholder]}>
        <Text style={styles.avatarPlaceholderText}>{initial}</Text>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* 标题 */}
      <View style={styles.header}>
        <PlatformIcon name="people" size={20} color={MODULE_COLOR} family="MaterialIcons" />
        <Text style={styles.title}>成员余额</Text>
      </View>

      {/* 成员列表 */}
      {balances.map((member) => {
        const progress = member.shouldPay > 0 
          ? Math.min(100, Math.max(0, (member.paid / member.shouldPay) * 100))
          : 0;

        return (
          <View key={member.userId} style={styles.memberItem}>
            {/* 头部：头像+名字 | 金额信息 */}
            <View style={styles.memberHeader}>
              <View style={styles.memberInfo}>
                {renderAvatar(member)}
                <Text style={styles.memberName} numberOfLines={1}>{member.displayName}</Text>
              </View>
              <View style={styles.memberAmounts}>
                <Text style={styles.amountText}>已支付: {formatCurrency(member.paid, currency)}</Text>
                <Text style={styles.amountText}>应付金额: {formatCurrency(member.shouldPay, currency)}</Text>
                <Text style={[
                  styles.balanceText,
                  member.balance >= 0 ? styles.balancePositive : styles.balanceNegative
                ]}>
                  {member.balance >= 0 ? '多付' : '欠款'}: {formatCurrency(Math.abs(member.balance), currency)}
                </Text>
              </View>
            </View>

            {/* 进度条 */}
            <View style={styles.progressRow}>
              <View style={styles.progressBar}>
                <View 
                  style={[
                    styles.progressFill, 
                    { 
                      width: `${progress}%`,
                      backgroundColor: member.balance >= 0 ? '#10B981' : '#F59E0B',
                    }
                  ]} 
                />
              </View>
              <Text style={styles.progressText}>{progress.toFixed(0)}%</Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  memberItem: {
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  memberHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  memberInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    marginRight: 12,
  },
  avatarPlaceholder: {
    backgroundColor: MODULE_COLOR,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarPlaceholderText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  memberName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    flex: 1,
  },
  memberAmounts: {
    alignItems: 'flex-end',
  },
  amountText: {
    fontSize: 13,
    color: '#6B7280',
    marginBottom: 2,
  },
  balanceText: {
    fontSize: 14,
    fontWeight: '600',
  },
  balancePositive: {
    color: '#10B981',
  },
  balanceNegative: {
    color: '#EF4444',
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  progressBar: {
    flex: 1,
    height: 8,
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  progressText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6B7280',
    minWidth: 40,
    textAlign: 'right',
  },
});

export default MemberBalanceSection;
